// The heat cell peek (round 086 card 12, R1825, ui-map-interface.md §14
// "The cell peek").
//
// While a heat mode paints, a hover on a desktop or a press on a telephone
// reads the heat of one cell: the cell takes an outline (the heatmap
// layer's own), and the card kit's peek states the mode, the cell, the
// value the server sent for that cell, its place on the ramp as a marker
// and a share, and the owner. While no mode paints, the peek of a cell is
// the water peek and carries no heat line.
//
// **The value is one byte, 0 to 255** (§14 "The subscription"). For SOV it
// is the cell's gauge on 0 to `gauge_max` (the server quantises the gauge
// onto the byte), so the peek writes the gauge. Every other mode has no
// unit: the byte states more or less of a thing, so the peek writes it as
// the byte of 255 and the share toward the mode's high end, and never a
// count.
//
// The module is pure: it turns one read of the field into a card spec.

import { HeatmapKind } from '../../gen/proto/aquaphobia';
import { card as S } from '../../gen/strings';
import { heatMode } from '../../state';
import { hex } from '../../world';
import { esc } from '../esc';
import { pctFig, ratioFig, rampCss } from '../format';
import { heldByFact, type CardSpec } from './card';
import { markSvg } from './marks';

/** What the peek reads of one cell. */
export interface HeatCellRead {
  /** The mode that paints; never NONE. */
  mode: HeatmapKind;
  cell: number;
  /** The byte the server sent for the cell, 0 to 255. */
  value: number;
  /** The faction that holds the cell, 0 for none. */
  owner: number;
  /** The cap of the sovereignty gauge (`Hello.catalog.rules.gauge_max`). */
  gaugeMax: number;
}

/** The share of the ramp a byte stands at, 0 to 1. */
export function heatShare(value: number): number {
  return Math.max(0, Math.min(255, value)) / 255;
}

/** The value fact: the gauge for SOV, else the byte of 255. */
export function heatValueFig(r: HeatCellRead): string {
  if (r.mode === HeatmapKind.HEATMAP_KIND_SOV && r.gaugeMax > 0) {
    return `${(heatShare(r.value) * r.gaugeMax).toFixed(1)}<small>/${r.gaugeMax}</small>`;
  }
  return ratioFig(Math.round(r.value), 255);
}

/** The peek of one heat cell. */
export function heatCellPeek(r: HeatCellRead): CardSpec {
  const mode = heatMode(r.mode);
  const share = heatShare(r.value);
  const at = (share * 100).toFixed(1);
  const mid = r.mode === HeatmapKind.HEATMAP_KIND_SOV
    ? pctFig(share * 100)
    : S.heat_toward({ pct: pctFig(share * 100), hi: esc(mode.hi) });
  return {
    size: 'peek', mark: markSvg('heat', hex(mode.color)), title: mode.label, ink: 'neutral', owner: r.owner,
    facts: [
      { key: S.f_value, val: heatValueFig(r) },
      heldByFact(r.owner),
    ],
    peekBody: '<div class="ck-sect ck-heat">'
      + `<i class="ck-ramp" style="background:${rampCss(mode)}"><b style="left:${at}%"></b></i>`
      + `<span class="ck-ends"><span>${esc(mode.lo)}</span><span>${mid}</span><span>${esc(mode.hi)}</span></span>`
      + '</div>',
  };
}
